import { MAX_ATTACHMENTS_PER_ASSET } from "./asset.js";
import { MAX_ISSUE_LINES } from "./stock.js";
import { GENERIC_ASSET_WORDS } from "./verticals.js";
/**
 * What one account may hold, as a ceiling against abuse.
 *
 * NOT A PRICING TIER, and deliberately not shown anywhere. VozQI is free, and
 * these two numbers exist so that "free" cannot mean "an unbounded amount of
 * somebody else's database". A real academy or bus operator is nowhere near
 * either of them: the sample business the product is demonstrated as has three
 * plans and twenty-five customers.
 *
 * INVISIBLE UNTIL SOMEBODY HITS ONE. There is no counter on any screen and no
 * "4 de 5 planes" anywhere, because a limit an honest user will never reach is
 * a limit that should not take up room in their head. What they get if they do
 * reach it is a sentence that says what happened and how to ask for more —
 * never a silent failure, and never a constraint name.
 *
 * PLANS COUNT INACTIVE ONES. Deactivating a plan keeps its row, its cobros and
 * its history, so it keeps costing what it cost; a cap that a deactivate could
 * free would not be a cap. Customers count the same way, for the same reason.
 *
 * THE CHECK IS NOT SERIALISED against a concurrent create, and that is a
 * decision rather than an oversight: two requests arriving together can both
 * read four plans and both write, leaving six. Taking a table lock on every
 * plan creation to prevent an occasional sixth row would be paying a real cost
 * forever to make an abuse ceiling exact — and an abuse ceiling does not need
 * to be exact, it needs to exist.
 */
export const MAX_PLANS_PER_BUSINESS = 5;
/**
 * People under one client -- a mother's children at the guardería. Counts
 * every member row, with or without a live plan: a baja keeps the row, its
 * cobros and its history, the same reason plans count inactive ones. Only
 * «Agregar» in the ficha can reach it; the alta and the import create one.
 */
export const MAX_MEMBERS_PER_PARTY = 3;
/**
 * Parties, not members: a row of Clientes is a household, and a mother with two
 * children enrolled is one customer. It is the same number `IMPORT_MAX_ROWS`
 * caps a single upload at, which is not a coincidence — one import can fill an
 * empty account exactly to the ceiling and no further.
 *
 * IT IS THE KERNEL'S, not `cuotas`'. Checked only where a cuota is involved
 * — `enrol` and the import — it would leave `createParty` free to walk an
 * iglesia past it one customer at a time.
 *
 * 1500, BECAUSE REAL BUSINESSES HAND OVER EXPORTS OF A THOUSAND CUSTOMERS. A
 * ceiling under the size of the list the product is for is not an abuse
 * ceiling, it is a door that does not open: the owner would have to split his
 * own file and delete rows off it. It is still an ABUSE ceiling and not a plan's — what a plan
 * sells is the período, and that is a row of its own.
 */
export const MAX_PARTIES_PER_BUSINESS = 1500;
// --------------------------------------------------------------- fiscal identity
/**
 * Column width for `business.tax_id` and `payer.tax_id` — a RUC or a cédula.
 * Panama's RUC has a compound form ("155646-1-155646") longer than a plain
 * number, so this is wider than `MAX_TAX_ID_DV_LENGTH` below.
 */
export const MAX_TAX_ID_LENGTH = 40;
/** Column width for `business.tax_id_dv` — the RUC's check digit, one or two characters. */
export const MAX_TAX_ID_DV_LENGTH = 8;
/** Column width for `business.legal_name` and `payer.legal_name` — razón social, distinct from a trading name. */
export const MAX_LEGAL_NAME_LENGTH = 160;
/** Column width for `business.address` and `party.address_line` — the same shape, reused rather than duplicated. */
export const MAX_ADDRESS_LENGTH = 240;
/** Column width for a party's three levels: `address_neighborhood`, `_locality`, `_region`. */
export const MAX_ADDRESS_PART_LENGTH = 80;
/** Column width for `party.address_postal_code`. */
export const MAX_POSTAL_CODE_LENGTH = 12;
/** Column width for `business.billing_point` — the DGI's punto de facturación code. */
export const MAX_BILLING_POINT_LENGTH = 60;
/**
 * Column width for `business.email` and `party.email`.
 *
 * IT MOVED HERE, IT WAS NOT INVENTED HERE. The same 160 lived privately inside
 * `import/normalize.ts`, where the customer import has always used it. The
 * business's own address needed a limit too, and two constants of the same
 * name holding the same number in two files is exactly how they stop being
 * the same number.
 */
export const MAX_EMAIL_LENGTH = 160;
// ------------------------------------------------------- what the paper says
/**
 * Column width for `business.payment_info` — «cómo te pagan», several lines
 * of free text printed at the foot of every document a customer receives.
 *
 * FREE TEXT AND NOT FOUR COLUMNS (bank, account kind, number, holder). Four
 * columns cannot hold a Yappy number, a second account or «pago contra
 * entrega», and they would make the app rule on what an account type is
 * called across nine countries. Four hundred characters is about eight lines,
 * which is more than the block SOLTEPSA prints and still far from a page.
 */
export const MAX_PAYMENT_INFO_LENGTH = 400;
/**
 * Column width for `business_service.warranty_default` and
 * `order_service.warranty` — the workshop's promise, as a sentence.
 *
 * A SENTENCE AND NOT A NUMBER OF DAYS. What a taller writes is «30 días en
 * mano de obra sobre trabajo realizado», and the qualifier is half the value:
 * an integer would need a second field to say the same thing, and the paper
 * would compose a sentence nobody wrote. The price: the document cannot say
 * «vence el 19 de diciembre», because it cannot count a sentence.
 */
export const MAX_WARRANTY_LENGTH = 240;
/**
 * The longest payment term a ticket may carry, in days -- the same ceiling as
 * the `check` on `payment_term_days` (0065). A year is already far beyond any
 * term a workshop gives; the cap exists so a typo («300» for «30») is not
 * silently accepted as a promise.
 */
export const MAX_PAYMENT_TERM_DAYS = 365;
/**
 * Lo más fuerte que el dueño puede poner la marca de agua, en por ciento.
 *
 * HAY UN TOPE PORQUE LA MARCA VA DETRÁS DE LOS NÚMEROS. El papel se dibuja en
 * ese orden a propósito —— la marca primero, el texto encima ——, así que subir
 * la opacidad no tapa nada; lo que hace es volver ilegible lo que hay encima,
 * que en esta app es un total. Un cuarto de tinta ya se ve desde el otro lado
 * de un mostrador y todavía deja leer una fila de nueve puntos.
 *
 * ESTE NÚMERO SE ESCRIBE UNA VEZ: lo leen el zod de `PATCH /business/watermark`
 * y los dos deslizadores. El `check` de la migración 0055 es más ancho a
 * propósito (0-100), porque una base de datos que conociera este 25 habría que
 * migrarla el día que el dueño lo mueva.
 */
export const MAX_WATERMARK_OPACITY = 25;
// --------------------------------------------------------------- attachments
/**
 * Attachments allowed on one cash entry or one debt: a single receipt is all
 * a movement ever needs, and one slot keeps the detail screen plain. The user-facing strings
 * that quote it say «archivo(s)», so raising it needs no wording
 * change.
 */
export const MAX_ATTACHMENTS_PER_ENTRY = 1;
/**
 * Attachments allowed on one TICKET (0036, spec §4.4) -- a different cap
 * from `MAX_ATTACHMENTS_PER_ENTRY`, on purpose. A receipt is one piece of
 * paper; a technician's own evidence of the work is several photos -- before,
 * after, a part that had to be replaced -- and a one-photo cap here would
 * refuse the second picture the spec's own example takes.
 *
 * Twenty, in all, across the ticket's objects and «Otras fotos»:
 * a before-and-after of several objects, plus the many photos the office uploads
 * from the technician's chat, has to fit. The cap is per ticket, not per
 * object, so the bulk move («Mover a…») never meets a second number.
 */
export const MAX_ATTACHMENTS_PER_ORDER = 20;
/** Photos of one catalogue item (spec 2026-09-22-cuotas-compartir-producto). */
export const MAX_ATTACHMENTS_PER_CATALOG_ITEM = 5;
/**
 * THE LARGEST A TICKET PHOTO MAY BE: 10 MB. At five, phone photos «cerca de
 * los 5mb» are refused by the backend without whoever uploads them
 * understanding why.
 *
 * LA COMPRESIÓN DEL SERVIDOR ES LA QUE HACE QUE DIEZ SIGA SIENDO UN TOPE Y NO
 * UNA AVERÍA (`confirmAttachment`, `kernel/ledger/attachments.ts`): el
 * original se recomprime al confirmarse la subida, así que este número sólo
 * decide qué tan grande puede llegar el archivo CRUDO del teléfono, nunca lo
 * que queda guardado. Diez megas es más que generoso para una foto de
 * cámara sin editar.
 *
 * PER FILE, NOT PER TICKET. AND ONLY THE TICKET'S: the Libro and the debts
 * have their own, `MAX_FILE_SIZE_BYTES` (`kernel/ledger/attachments.ts`).
 */
export const MAX_ORDER_FILE_SIZE_BYTES = 10 * 1024 * 1024;
/**
 * What a file IS to the thing it hangs off, and how many of them fit.
 *
 * ONE SWITCH, CLIENT-SIDE, MIRRORING THE SERVER'S OWN (`attachments.ts`'s
 * `ownerOf`). A movement keeps a comprobante and a ticket keeps FOTOS del
 * trabajo (spec §4.4) -- different words, and different caps -- and a screen
 * that hardcoded either would tell a technician «máximo 1 archivo por
 * movimiento» about a ticket that takes ten photos.
 *
 * IT DOES NOT ENFORCE ANYTHING. The cap is the server's, checked on every
 * `requestUpload`; this is what lets a screen stop offering the drop zone
 * once it is full, and say why in the owner's own nouns.
 */
export function attachmentWordsFor(kind) {
    switch (kind) {
        case "entry":
            return { cap: MAX_ATTACHMENTS_PER_ENTRY, one: "comprobante", owner: "movimiento", none: "Sin comprobantes todavía.", covers: false };
        case "debt":
            return { cap: MAX_ATTACHMENTS_PER_ENTRY, one: "comprobante", owner: "deuda", none: "Sin comprobantes todavía.", covers: false };
        case "order":
            return { cap: MAX_ATTACHMENTS_PER_ORDER, one: "foto", owner: "pedido", none: "Sin fotos todavía.", covers: false };
        case "catalogItem":
            return { cap: MAX_ATTACHMENTS_PER_CATALOG_ITEM, one: "foto", owner: "artículo", none: "Sin fotos todavía.", covers: true };
        case "asset":
            // `owner` is never drawn for an object: its photos go through
            // PhotoGallery, which has no «máximo por …» sentence, and the server's
            // refusals carry the trade's word (`assetSentencesFor`).
            return { cap: MAX_ATTACHMENTS_PER_ASSET, one: "foto", owner: GENERIC_ASSET_WORDS.one, none: "Sin fotos todavía.", covers: false };
    }
}
// --------------------------------------------------------------- number words
/**
 * Spanish number words, for a ceiling sentence that has to read as a
 * sentence a person wrote -- «Ya tienes tres negocios», not a template with
 * a digit spliced in. Falls back to the digit itself past this table, rather
 * than growing it for a count nobody will ever hit.
 */
const NUMBER_WORDS = {
    1: "un",
    2: "dos",
    3: "tres",
    4: "cuatro",
    5: "cinco",
};
// --------------------------------------------------------------- team
/**
 * People an owner can invite into one business, live invitations included.
 * The owner is not counted. Shown when reached, unlike the ceilings above:
 * a bakery with a cashier, two baking, a driver and a bookkeeper
 * sits exactly on it, so it is a product limit rather than an abuse ceiling.
 */
export const MAX_TEAM_MEMBERS = 5;
/** Active accountants one business may have (spec 2026-10-04 §4); none takes a seat. */
export const MAX_ACCOUNTANTS = 3;
/** What the alta and the server say once the accountants are full. */
export const ACCOUNTANTS_FULL_HINT = "Ya tienes 3 contables. Da de baja a uno para invitar a otro.";
/**
 * Seats still free on this business's plan.
 *
 * A SEAT PROMISED IS A SEAT GONE. A pending
 * invitation costs one, exactly like a membership does: without that, the
 * owner hands out a sixth code on a five-seat plan, the server refuses it at
 * redemption -- «dos escrituras y la primera manda» -- and the person who
 * finds out is whoever typed the code, not the person who sent it.
 *
 * The ficha and «Empleados» both read `listInvitations` to say «Invitado el
 * …», so the number is in hand.
 *
 * REVOKED AND EXPIRED CODES FREE THE SEAT WITH NO RULE OF THEIR OWN:
 * `listInvitations` serves only live ones (`accepted_at is null and
 * revoked_at is null and expires_at > now()`), so a code that died is simply
 * not in the list. That is what makes counting them safe.
 *
 * ONE PERSON, ONE SEAT, however they arrive: somebody holding a membership
 * AND a stray invitation is counted once. THE OWNER NEVER INVITED THEMSELVES
 * and THE ACCOUNTANT TAKES NO SEAT (spec §3.9) -- not while his code is
 * outstanding either.
 */
export function teamSeatsLeft(people, pendingInvitations) {
    const used = people.filter((person) => !person.isOwner &&
        !person.isAccountant &&
        (person.membershipId !== null || pendingInvitations.some((i) => i.employeeId === person.id))).length;
    return Math.max(0, MAX_TEAM_MEMBERS - used);
}
/**
 * What «Invitar» says once the seats are gone.
 *
 * WORDS, NOT UI, which is the same reason `LIBRO_QUESTION` and `NADA_HINT`
 * live in capabilities.ts: «no compartas UI entre web y móvil» is a rule
 * about markup, and it never covered the sentences the markup prints. Four
 * screens show this one -- «Empleados» and the ficha, in each client -- and
 * typed out in each, the day `MAX_TEAM_MEMBERS` moves, whichever one is
 * missed keeps saying «cinco» and is the one somebody reads.
 *
 * THE NUMBER IS READ OFF THE CEILING, spelled out like the businesses one:
 * a sentence with a digit in it reads as a template.
 *
 * AND IT NAMES NO PLAN, NOR THE WORD «plan». «Tu plan permite…» would tell
 * an iPhone user there are plans to move up to, and there are none: these
 * ceilings are
 * against abuse, as the terms of use say, and a hint at an upgrade the app
 * cannot sell in-app is what App Review rejects (3.1.1). The seats belong to
 * the team, so the sentence says the team.
 */
export const SEATS_FULL_HINT = `Puedes tener hasta ${NUMBER_WORDS[MAX_TEAM_MEMBERS] ?? String(MAX_TEAM_MEMBERS)} personas en tu equipo.`;
/**
 * WHAT GIVING SOMEBODY ACCESS COSTS, said before it is given.
 *
 * THE MIRROR OF THE ACCOUNTANT'S OWN LINE. His block offers the same shape --
 * a question, a short consequence, a button -- and its consequence is that it
 * takes no seat, precisely because his invitation takes no seat
 * (spec §3.9). Everybody else's does, and that is the fact the owner weighs
 * before deciding.
 *
 * NULL RATHER THAN A SECOND SENTENCE ABOUT THE SAME NUMBER: with the seats
 * gone the card already has `SEATS_FULL_HINT`, so this one falls silent and
 * the caller writes `seatsLeftHint(n) ?? SEATS_FULL_HINT` -- one line in that
 * slot, never two saying the same thing.
 *
 * `NUMBER_WORDS` HAS «un», NOT «uno», because it was written for «hasta cinco
 * personas», where the word is an article. Here it ends the sentence, where
 * Spanish wants the pronoun -- and the verb agrees with it.
 */
export function seatsLeftHint(seatsLeft) {
    if (seatsLeft <= 0)
        return null;
    const word = seatsLeft === 1 ? "uno" : (NUMBER_WORDS[seatsLeft] ?? String(seatsLeft));
    return `Ocupa un lugar de tu equipo: te ${seatsLeft === 1 ? "queda" : "quedan"} ${word}.`;
}
// --------------------------------------------------------------- businesses
/**
 * Businesses one account may own. Unlike the ceilings above this one is
 * SHOWN when reached: an owner with a bakery, an academy and a workshop is
 * a real person, not an abuser, and the sentence tells them to delete one
 * before creating another. Businesses the person was invited into are
 * somebody else's and do not count.
 */
export const MAX_BUSINESSES_PER_OWNER = 3;
/**
 * How many businesses in a list of memberships one account itself owns --
 * the ones that count against `MAX_BUSINESSES_PER_OWNER`. A membership
 * somebody else granted (`role: "member"`) is theirs, not this account's,
 * and does not count.
 *
 * Here, beside the ceiling it exists to check, so the two clients cannot drift on when «Crear otro
 * negocio» gives way to the ceiling sentence.
 */
export function ownedCount(memberships) {
    return memberships.filter((m) => m.role === "owner").length;
}
/**
 * `MAX_BUSINESSES_PER_OWNER`, spelled out in Spanish -- what both clients'
 * ceiling sentence and the backend's `TOO_MANY` message interpolate, so a
 * change to the constant never leaves the word behind.
 */
export const MAX_BUSINESSES_PER_OWNER_WORD = NUMBER_WORDS[MAX_BUSINESSES_PER_OWNER] ?? String(MAX_BUSINESSES_PER_OWNER);
/**
 * THE CUSTOMER NUMBER'S CEILING (0063). Nine digits keep it well inside
 * `integer`, and leave the import room to park a party's number above it
 * while a file renumbers everybody (see `applyNumbers`).
 */
export const MAX_PARTY_NUMBER = 999_999_999;
/**
 * Active money accounts one business may have. An
 * archived one frees its slot: it keeps its history, but no form offers it.
 * Unlike the plans above, the server counts this one under the business's
 * row lock, so two creates at nine end at ten.
 */
export const MAX_MONEY_ACCOUNTS = 10;
/** What «Nueva cuenta» and the server say once the accounts are full. */
export const MONEY_ACCOUNTS_FULL_HINT = `Ya tienes ${MAX_MONEY_ACCOUNTS} cuentas. Archiva una que no uses para crear otra.`;
/** How many more accounts this business can open: only the active ones count. */
export function moneyAccountsLeft(accounts) {
    return Math.max(0, MAX_MONEY_ACCOUNTS - accounts.filter((account) => !account.archived).length);
}
/** How many more accountants this business can add: only the active ones count toward the cap. */
export function accountantsLeft(people) {
    return Math.max(0, MAX_ACCOUNTANTS - people.filter((p) => p.isAccountant && p.active).length);
}
/**
 * A DOCUMENT'S LINES, at most the stock issue they become: every line of a
 * sale can leave the shelf, and one issue takes `MAX_ISSUE_LINES`.
 */
export const MAX_DOCUMENT_LINES = MAX_ISSUE_LINES;
/**
 * A LIST OF IDS IN ONE REQUEST -- the services a profile leaves out, the
 * people a ticket is assigned to -- where no other ceiling names its size.
 * Far past what any screen draws, and short of a body that costs anything.
 */
export const MAX_IDS_PER_REQUEST = 500;
